// Fit and Engagement on the board (GRO-381): the join onto book rows, the readings the columns
// and the hover draw, and the sort values. Plain .ts so node --test can load it.
//
// A row with no score (an account not scored yet, or scores hidden for this deploy) has
// `score: null`. Every reading returns null for it, and sort.ts pushes null last in both
// directions, so a missing score never reads as 0.

import type { AccountScore, BookAccount, ScoreCategory, ScoresPayload } from '../api/types.ts';

export interface ScoredFields {
  score?: AccountScore | null;
  // The score-order rank (rankByScore), set on the rows a table shows; null when unscored.
  score_rank?: number | null;
}

export type ScoreTone = 'good' | 'warn' | 'bad' | 'info' | 'neutral';

export interface BucketReading {
  label: string;
  tone: ScoreTone;
  rank: number;
}

export interface ReadinessReading {
  label: string;
  tone: ScoreTone;
  rank: number;
}

const LEVEL_RANK: Record<string, number> = { low: 0, mid: 1, high: 2 };

const READINESS_LABELS: Record<string, ReadinessReading> = {
  too_early: { label: 'Too early', tone: 'neutral', rank: 1 },
  startup: { label: 'Startup', tone: 'info', rank: 2 },
  growth: { label: 'Growth', tone: 'good', rank: 3 },
  enterprise: { label: 'Enterprise', tone: 'good', rank: 4 },
  unknown: { label: 'Unknown', tone: 'neutral', rank: 0 },
};

// The scores are joined only when the server says they are visible; otherwise the rows come
// back as the same array, so the board's memoised rows do not rebuild.
export function attachScores<Row extends BookAccount>(
  rows: Row[],
  payload: ScoresPayload | null | undefined,
): (Row & ScoredFields)[] {
  if (!payload || !payload.visible) return rows;
  const byAccountId = payload.by_account_id;
  return rows.map(row => ({ ...row, score: byAccountId[row.id] ?? null }));
}

export function scoreOf(row: BookAccount): AccountScore | null {
  return (row as BookAccount & ScoredFields).score ?? null;
}

// How long a board holds its table for the scores before drawing it without them (GRO-491).
export const SCORES_WAIT_MILLISECONDS = 10_000;

// Whether a board shows its loading line rather than the table: the scores are shown to this
// viewer and still on their way. Drawing the rows first and then the Fit and Engagement columns
// re-sorted the table under the reader (GRO-491). A failed read settles, and so does a wait that
// ran past SCORES_WAIT_MILLISECONDS (`gaveUp`); both draw the table without scores.
export function holdForScores(scoresVisible: boolean, scoresPending: boolean, gaveUp: boolean): boolean {
  return scoresVisible && scoresPending && !gaveUp;
}

// Whether the board has any score to draw: the columns render only then.
export function anyScores(payload: ScoresPayload | null | undefined): boolean {
  return Boolean(payload && payload.visible && Object.keys(payload.by_account_id).length > 0);
}

export function formatScore(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(Math.round(value));
}

export function levelLabel(level: string | null | undefined): string {
  if (!level) return '';
  return level.charAt(0).toUpperCase() + level.slice(1);
}

export function fitSortValue(row: BookAccount): number | null {
  return scoreOf(row)?.fit_score ?? null;
}

export function engagementSortValue(row: BookAccount): number | null {
  return scoreOf(row)?.engagement_score ?? null;
}

// --- colour levels and the table rank -----------------------------------------------------

export type ScoreLevel = 'low' | 'mid' | 'high';

// Cut-offs approved by Vikram on 22 Oct (GRO-459), copied from `levels` in scoring/settings.json:
// below low_below is low, at or above high_from is high. The scores payload carries no thresholds,
// so a change in settings.json has to be made here too.
export const SCORE_LEVEL_CUTOFFS = {
  fit: { low_below: 32, high_from: 68 },
  engagement: { low_below: 25, high_from: 80 },
};

export function scoreLevel(kind: 'fit' | 'engagement', value: number | null | undefined): ScoreLevel | null {
  if (value == null || !Number.isFinite(value)) return null;
  const cutoffs = SCORE_LEVEL_CUTOFFS[kind];
  if (value < cutoffs.low_below) return 'low';
  if (value >= cutoffs.high_from) return 'high';
  return 'mid';
}

// The one weighting behind the table's # column: Fit counts twice, Engagement once.
export const RANK_FIT_WEIGHT = 2;
export const RANK_ENGAGEMENT_WEIGHT = 1;

// Null when the account has no Fit (no score yet); a missing Engagement counts as 0.
export function rankScore(score: AccountScore | null | undefined): number | null {
  const fit = score?.fit_score;
  if (fit == null || !Number.isFinite(fit)) return null;
  const engagement = score?.engagement_score ?? 0;
  return RANK_FIT_WEIGHT * fit + RANK_ENGAGEMENT_WEIGHT * engagement;
}

// Ranks 1..N over exactly the rows given, highest rankScore first, ties by account name so the
// order is stable. Unscored rows are absent from the map (no rank). Pass the rows a table shows.
export function rankByScore(rows: BookAccount[]): Map<string, number> {
  const scored: { id: string; name: string; value: number }[] = [];
  for (const row of rows) {
    const value = rankScore(scoreOf(row));
    if (value != null) scored.push({ id: row.id, name: row.account, value });
  }
  scored.sort((first, second) => (second.value - first.value) || first.name.localeCompare(second.name));
  return new Map(scored.map((entry, index) => [entry.id, index + 1]));
}

export function attachScoreRanks<Row extends BookAccount>(rows: Row[]): (Row & ScoredFields)[] {
  const ranks = rankByScore(rows);
  return rows.map(row => ({ ...row, score_rank: ranks.get(row.id) ?? null }));
}

export function scoreRankOf(row: BookAccount): number | null {
  return (row as BookAccount & ScoredFields).score_rank ?? null;
}

// "mid fit / low engagement" as "Mid · Low". Tinted by how worth working the pair is.
export function bucketFromScore(score: AccountScore | null): BucketReading | null {
  if (!score || !score.bucket || !score.fit_level || !score.engagement_level) return null;
  const fitRank = LEVEL_RANK[score.fit_level] ?? 0;
  const engagementRank = LEVEL_RANK[score.engagement_level] ?? 0;
  let tone: ScoreTone = 'neutral';
  if (fitRank === 2 && engagementRank === 2) tone = 'good';
  else if (fitRank === 2) tone = 'info';
  else if (engagementRank === 2) tone = 'warn';
  return {
    label: `${levelLabel(score.fit_level)} · ${levelLabel(score.engagement_level)}`,
    tone,
    rank: fitRank * 3 + engagementRank,
  };
}

export function bucketReading(row: BookAccount): BucketReading | null {
  return bucketFromScore(scoreOf(row));
}

export function bucketSortValue(row: BookAccount): number | null {
  return bucketReading(row)?.rank ?? null;
}

export function readinessFromScore(score: AccountScore | null): ReadinessReading | null {
  const band = score?.readiness_band;
  if (!band) return null;
  const known = READINESS_LABELS[band];
  if (known) return known;
  return { label: band.replace(/_/g, ' '), tone: 'neutral', rank: 0 };
}

export function readinessReading(row: BookAccount): ReadinessReading | null {
  return readinessFromScore(scoreOf(row));
}

// Names a person sees for category keys that read badly spaced out. `playground` is shown as Self-serve
// (GRO-431); `social` is the LinkedIn Companies Hub engagement category (GRO-379), a key that predates its source.
// Fit's voice category was split in two on 26 Oct (GRO-465); scores stored before then still hold one
// `voice_product` line, so the old key keeps its name.
const CATEGORY_LABELS: Record<string, string> = {
  ai_team: 'AI team', tech_native: 'Tech / AI-native', tech_ai_native: 'Tech / AI-native',
  social: 'LinkedIn engagement', playground: 'Self-serve',
  voice_is_product: 'Voice is their product', voice_use_case: 'Clear voice use case', voice_product: 'Voice product',
};

export function categoryLabel(category: string): string {
  const named = CATEGORY_LABELS[category];
  if (named) return named;
  const spaced = category.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// The category's number for the hover: "74", or "30 · data missing" when it fell to the default.
export function categoryValue(category: ScoreCategory & { weight?: number | null }): string {
  const value = formatScore(category.score);
  const weight = category.weight == null ? '' : ` · weight ${formatScore(category.weight)}`;
  if (category.used_default && category.weight === 0) return 'data missing · left out of the score';
  return category.used_default ? `${value} · data missing${weight}` : `${value}${weight}`;
}

export function missingDataCategories(categories: ScoreCategory[]): string[] {
  // A category not yet measured is shown as such, never as "data missing".
  return categories.filter(category => category.used_default && category.enabled !== false).map(category => categoryLabel(category.category));
}

// The CSV's cells: blank for no score, the number rounded as the board shows it.
export function fitCsvValue(row: BookAccount): string {
  const value = fitSortValue(row);
  return value == null ? '' : formatScore(value);
}

export function engagementCsvValue(row: BookAccount): string {
  const value = engagementSortValue(row);
  return value == null ? '' : formatScore(value);
}

// The same "Mid · Low" the board's pill shows, so the download matches the screen.
export function bucketCsvValue(row: BookAccount): string {
  return bucketReading(row)?.label ?? '';
}
